import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { useSEO } from "@/lib/seo";

const LAST_UPDATED = "September 2026";

const Section = ({ heading, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl md:text-2xl text-neutral-900">{heading}</h2>
    <div className="mt-3 space-y-3 text-sm md:text-base text-neutral-600 leading-relaxed">{children}</div>
  </section>
);

const PolicyLayout = ({ eyebrow, title, intro, testid, path, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: `${title} | Gold Time Jewelry`,
    canonicalPath: path,
    description: `${title} for Gold Time Jewelry — family-owned Texas jeweler since 1991, showrooms in Katy, Copperas Cove and Killeen.`,
  });

  return (
    <main data-testid={testid}>
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>{eyebrow}</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid={`${testid}-heading`}>
              {title}
            </h1>
            <p className="text-xs text-neutral-400 mt-3">Last updated: {LAST_UPDATED}</p>
            <p className="text-sm md:text-base text-neutral-600 leading-relaxed mt-6">{intro}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-4">{children}</div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-14 bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid={`${testid}-contact`}>
              <h2 className="font-display text-lg text-neutral-900">Questions?</h2>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                Call or visit any Gold Time Jewelry showroom — Katy (346) 307-7864 · Copperas Cove (254) 547-4653 ·
                Killeen (254) 539-4653 — or send us a note through our{" "}
                <Link to="/#contact" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">
                  contact form
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export const TermsPage = () => (
  <PolicyLayout
    eyebrow="The Fine Print"
    title="Terms & Conditions"
    testid="terms-page"
    path="/terms"
    intro="Welcome to Gold Time Jewelry. By browsing our site, placing an order, or selling your gold, watches, or diamonds to us, you agree to the terms below."
  >
    <Section heading="1. Who we are">
      <p>
        Gold Time Jewelry is a family-owned Texas company with showrooms in Katy, Copperas Cove, and Killeen. We buy
        and sell gold, luxury and mid-range watches, diamonds, and fine jewelry, and we perform jewelry and watch
        repair, custom design, and ring sizing.
      </p>
    </Section>
    <Section heading="2. Products, pricing & availability">
      <p>
        We work hard to keep photos, descriptions, prices, and availability accurate, but errors can happen and
        stock can change. If an item you order becomes unavailable or was mispriced, we will contact you promptly
        and offer a full refund or a comparable alternative — the choice is yours.
      </p>
      <p>
        Manufacturer suggested retail prices (MSRP), when shown, are reference prices and are displayed only when
        our price is lower.
      </p>
    </Section>
    <Section heading="3. Ring sizing & custom work">
      <p>
        Rings stocked in a single size can be sized before shipping. Sizing fees are shown before
        you add the ring to your cart: $25 covers sizing up to two sizes up or any size down, and each additional
        size beyond two up is $15. We offer sizing from size 4 to size 12; sizes outside that range require a custom
        quote — please contact us first.
      </p>
      <p>
        Any ring we size to order is a customized piece and is{" "}
        <Link to="/refund-policy" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">
          non-refundable
        </Link>
        , as are custom designs, CAD work, and engraved items.
      </p>
    </Section>
    <Section heading="4. Orders & payment">
      <p>
        Payment is processed securely by Stripe; we never see or store your card number. We reserve the right to
        refuse or cancel any order, including orders placed at an incorrect price, with a full refund issued to your
        original payment method.
      </p>
    </Section>
    <Section heading="5. Shipping & pickup">
      <p>
        Every order ships insured within the United States, or you can pick up free at any of our three showrooms.
        A signature may be required on delivery for higher-value pieces. Risk of loss passes to you upon confirmed
        delivery to the address you provide.
      </p>
    </Section>
    <Section heading="6. Selling your gold, watches & diamonds to us">
      <p>
        All testing and weighing is done openly in front of you, and every offer is obligation-free. A valid
        government-issued photo ID is required for all sales to us, as required by Texas law. Once you accept an
        offer and are paid, the sale is final.
      </p>
    </Section>
    <Section heading="7. Intellectual property">
      <p>
        All content on this site — text, design, logos, and imagery — belongs to Gold Time Jewelry or its licensors
        and may not be reproduced without written permission.
      </p>
    </Section>
    <Section heading="8. Limitation of liability">
      <p>
        To the fullest extent permitted by law, Gold Time Jewelry is not liable for indirect or consequential damages
        arising from use of this site or purchase of products. Our total liability for any claim is limited to the
        amount you paid for the item in question.
      </p>
    </Section>
    <Section heading="9. Governing law">
      <p>These terms are governed by the laws of the State of Texas, without regard to conflict-of-law rules.</p>
    </Section>
  </PolicyLayout>
);

export const PrivacyPage = () => (
  <PolicyLayout
    eyebrow="Your Privacy"
    title="Privacy Policy"
    testid="privacy-page"
    path="/privacy"
    intro="Gold Time Jewelry respects your privacy. This policy explains what we collect, why, and how it's protected."
  >
    <Section heading="1. What we collect">
      <ul className="list-disc pl-5 space-y-2">
        <li>Contact details you share through our enquiry form, phone calls, or WhatsApp messages.</li>
        <li>Order information — items purchased, shipping address, and sizing requests.</li>
        <li>Basic browsing data such as pages visited, collected through standard cookies and analytics.</li>
      </ul>
    </Section>
    <Section heading="2. Payments">
      <p>
        Card payments are processed entirely by Stripe. Your card number never touches our servers and is never
        stored by us.
      </p>
    </Section>
    <Section heading="3. How we use your information">
      <ul className="list-disc pl-5 space-y-2">
        <li>To answer enquiries and prepare valuations or offers you request.</li>
        <li>To fulfill, ship, and service your orders — including sizing and repair services.</li>
        <li>To meet legal obligations related to precious-metals purchases in Texas.</li>
      </ul>
    </Section>
    <Section heading="4. Who we share it with">
      <p>
        Only the partners needed to serve you: our payment processor (Stripe) and the shipping carriers who
        deliver your order. We never sell your personal information.
      </p>
    </Section>
    <Section heading="5. Cookies & analytics">
      <p>
        We use a small number of cookies to keep your cart working and to understand how the site is used. You can
        disable cookies in your browser; the catalog will still work, though your cart may not persist.
      </p>
    </Section>
    <Section heading="6. Security & retention">
      <p>
        We limit access to your information to team members who need it and keep it only as long as needed for your
        order, our records, or legal requirements.
      </p>
    </Section>
    <Section heading="7. Your rights">
      <p>
        You may ask us to see, correct, or delete the personal information we hold about you at any time — just call
        or visit any showroom, or reach us through the contact form.
      </p>
    </Section>
    <Section heading="8. Children">
      <p>Our site and services are intended for adults. We do not knowingly collect information from children.</p>
    </Section>
    <Section heading="9. Changes to this policy">
      <p>If we update this policy, the revised version will be posted here with a new "last updated" date.</p>
    </Section>
  </PolicyLayout>
);

export const RefundPolicyPage = () => (
  <PolicyLayout
    eyebrow="Shop With Confidence"
    title="Refund Policy"
    testid="refund-page"
    path="/refund-policy"
    intro="We want you to love your piece. If something isn't right, here's exactly how returns and refunds work at Gold Time Jewelry."
  >
    <Section heading="1. 30-day returns">
      <p>
        Unworn items in original condition, with original packaging and proof of purchase, may be returned within{" "}
        <strong className="text-neutral-800">30 days</strong> of delivery or pickup for a full refund to your
        original payment method.
      </p>
    </Section>
    <Section heading="2. Non-refundable items">
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong className="text-neutral-800">Rings that have been sized.</strong> Any ring sized to your requested
          size by our jewelers is a customized piece and cannot be refunded.
        </li>
        <li>Custom designs, CAD-designed pieces, and engraved items.</li>
        <li>Special-order pieces sourced specifically at your request.</li>
        <li>Items that show wear, damage, or alteration after delivery.</li>
      </ul>
    </Section>
    <Section heading="3. How to start a return">
      <p className="mb-4">
        <Link
          to="/returns"
          data-testid="start-a-return"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-7 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] transition-[transform] duration-300 hover:-translate-y-0.5"
        >
          Start a Return
        </Link>
      </p>
      <p>
        Send us the online form above, or call/visit any showroom — Katy (346) 307-7864, Copperas Cove
        (254) 547-4653, or Killeen (254) 539-4653 — or message us on WhatsApp with your order details.
        Submitting a request does not by itself authorize a return: we'll review it under this policy, walk
        you through it and, for shipped orders, provide insured return instructions. Please don't ship
        anything back before talking to us.
      </p>
    </Section>
    <Section heading="4. Refund timing">
      <p>
        Once we receive and inspect your return, refunds are issued to your original payment method within 5–10
        business days. Your bank may take a few extra days to post it.
      </p>
    </Section>
    <Section heading="5. Damaged or incorrect items">
      <p>
        If your piece arrives damaged or isn't what you ordered, contact us within 7 days and we'll make it right —
        repair, replacement, or full refund, including return shipping.
      </p>
    </Section>
    <Section heading="6. Exchanges">
      <p>
        Prefer a different piece? Unworn, non-customized items can be exchanged within the same 30-day window —
        we'll happily help you find the right one.
      </p>
    </Section>
  </PolicyLayout>
);
